import React, { useState, useEffect } from 'react';
import { Coffee, Sparkles, Zap, Brain, Target } from 'lucide-react';

interface LoadingScreenProps {
  onComplete: () => void;
}

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const messages = [
    "🔥 Adam's still debating the roast profile. Chris suggested ChatGPT pick one. Now the AI is crying.",
    "🤖 Chris asked AI to finish your quiz. It tried to order Coors Light beans. We're recalibrating.",
    "🐳 Chris said: 'What if the quiz results were whale-safe?' Adam's still figuring out what that means."
  ];

  useEffect(() => {
    // Message 1 shows immediately (index 0)
    // Message 2 shows after 4 seconds (index 1)
    const timer1 = setTimeout(() => {
      setCurrentMessageIndex(1);
    }, 4000);
    
    // Message 3 shows after 8 seconds (index 2)
    const timer2 = setTimeout(() => {
      setCurrentMessageIndex(2);
    }, 8000);
    
    // After 12 seconds total, the parent component will call onComplete
    // We don't need to call onComplete here since QuizModal handles the timing

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onComplete]);

  return (
    <div className="text-center space-y-8 py-8">
      {/* Spinning Coffee Wheel */}
      <div className="relative mx-auto w-32 h-32 mb-8">
        <div className="absolute inset-0 rounded-full border-4 border-[#fda36b]/20"></div>
        <div 
          className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#fda36b] animate-spin"
          style={{ animationDuration: '1s' }}
        ></div>
        <div 
          className="absolute inset-2 rounded-full border-4 border-transparent border-r-[#fda36b]/60 animate-spin"
          style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}
        ></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <Coffee className="w-12 h-12 text-[#fda36b] animate-pulse" />
        </div>
        
        {/* Floating icons around the wheel */}
        <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-bounce" style={{ animationDelay: '0s' }} />
        <Zap className="absolute -bottom-2 -left-2 w-6 h-6 text-blue-400 animate-bounce" style={{ animationDelay: '0.5s' }} />
        <Brain className="absolute top-1/2 -left-8 w-6 h-6 text-purple-400 animate-bounce" style={{ animationDelay: '1s' }} />
        <Target className="absolute top-1/2 -right-8 w-6 h-6 text-green-400 animate-bounce" style={{ animationDelay: '1.5s' }} />
      </div>

      {/* Title */}
      <div className="space-y-4">
        <h2 className="text-3xl font-bold text-white">
          Analyzing your coffee personality...
        </h2>
      </div>

      {/* Current Message */}
      <div className="min-h-[4rem] flex items-center justify-center">
        <div className="h-16 flex items-center justify-center">
          <p className="text-xl text-white/90 font-medium">
            {messages[currentMessageIndex]}
          </p>
        </div>
      </div>
    </div>
  );
}